import type { Cell, Grid, Piece, Pos, Special, TileKind } from './types';
import type { Rng } from './rng';

/** Letter codes used by level files and tests. */
export const KIND_CODE: Record<string, TileKind> = {
  l: 'latte', c: 'croissant', u: 'cupcake', m: 'macaron', d: 'donut', k: 'cookie',
};
export const CODE_OF_KIND = Object.fromEntries(Object.entries(KIND_CODE).map(([c, k]) => [k, c])) as Record<TileKind, string>;
const SPECIAL_CODE: Record<string, Special> = { '-': 'stripeH', '|': 'stripeV', '*': 'bomb' };
const CODE_OF_SPECIAL: Record<Special, string> = { stripeH: '-', stripeV: '|', bomb: '*', colorstar: 'S' };

/**
 * The playing field: rows × cols cells, each with an optional piece, mud
 * blocker, paw stain, or marked as hole. Pure data + helpers, no rules.
 */
export class Board {
  readonly cells: Cell[];
  private nextId = 1;

  constructor(readonly rows: number, readonly cols: number) {
    this.cells = Array.from({ length: rows * cols }, () => ({ hole: false, piece: null, blocker: null, stain: false }));
  }

  /**
   * Builds a board from text rows of space-separated tokens:
   *   l c u m d k   tile kinds (latte, croissant, cupcake, macaron, donut, cookie)
   *   c- c| c*      croissant with horizontal stripe / vertical stripe / bomb
   *   S             colorstar
   *   X x           mud with 2 / 1 hits left
   *   #             hole (not part of the board)
   *   .             empty (filled at random by the level loader)
   *   ~c  ~X        leading ~ = paw stain under this cell
   */
  static parse(rows: string[]): Board {
    const tokens = rows.map((r) => r.trim().split(/\s+/));
    const b = new Board(tokens.length, tokens[0].length);
    tokens.forEach((line, r) => {
      if (line.length !== b.cols) throw new Error(`row ${r} has ${line.length} cells, expected ${b.cols}`);
      line.forEach((tok, c) => {
        const cell = b.get(r, c);
        if (tok.startsWith('~')) {
          cell.stain = true;
          tok = tok.slice(1);
        }
        if (tok === '#') cell.hole = true;
        else if (tok === 'X' || tok === 'x') cell.blocker = { type: 'mud', hp: tok === 'X' ? 2 : 1 };
        else if (tok === 'S') cell.piece = b.newPiece(null, 'colorstar');
        else if (tok !== '.') {
          const kind = KIND_CODE[tok[0]];
          if (!kind) throw new Error(`unknown token "${tok}" at ${r},${c}`);
          cell.piece = b.newPiece(kind, tok[1] ? SPECIAL_CODE[tok[1]] ?? null : null);
        }
      });
    });
    return b;
  }

  /** Inverse of parse (handy for debugging and snapshot tests). */
  format(): string[] {
    const out: string[] = [];
    for (let r = 0; r < this.rows; r++) {
      const line: string[] = [];
      for (let c = 0; c < this.cols; c++) {
        const cell = this.get(r, c);
        let t = '.';
        if (cell.hole) t = '#';
        else if (cell.blocker) t = cell.blocker.hp >= 2 ? 'X' : 'x';
        else if (cell.piece) {
          const p = cell.piece;
          t = p.special === 'colorstar' ? 'S' : CODE_OF_KIND[p.kind!] + (p.special ? CODE_OF_SPECIAL[p.special] : '');
        }
        line.push((cell.stain ? '~' : '') + t);
      }
      out.push(line.join(' '));
    }
    return out;
  }

  clone(): Board {
    const b = new Board(this.rows, this.cols);
    b.nextId = this.nextId;
    this.cells.forEach((c, i) => {
      b.cells[i] = { hole: c.hole, stain: c.stain, piece: c.piece && { ...c.piece }, blocker: c.blocker && { ...c.blocker } };
    });
    return b;
  }

  newPiece(kind: TileKind | null, special: Special | null = null): Piece {
    return { id: this.nextId++, kind, special };
  }

  inside(p: Pos): boolean {
    return p.row >= 0 && p.row < this.rows && p.col >= 0 && p.col < this.cols;
  }

  get(row: number, col: number): Cell {
    return this.cells[row * this.cols + col];
  }

  at(p: Pos): Cell {
    return this.get(p.row, p.col);
  }

  /** A cell that can hold a (falling) piece: on the board and not blocked. */
  isOpen(p: Pos): boolean {
    if (!this.inside(p)) return false;
    const c = this.at(p);
    return !c.hole && !c.blocker;
  }

  /** Kind at p if a matchable tile sits there (colorstars and mud never match). */
  kindAt(row: number, col: number): TileKind | null {
    if (row < 0 || row >= this.rows || col < 0 || col >= this.cols) return null;
    const c = this.get(row, col);
    return c.blocker || !c.piece ? null : c.piece.kind;
  }

  positions(): Pos[] {
    const out: Pos[] = [];
    for (let row = 0; row < this.rows; row++) for (let col = 0; col < this.cols; col++) out.push({ row, col });
    return out;
  }

  count(pred: (c: Cell) => boolean): number {
    return this.cells.filter(pred).length;
  }

  toGrid(): Grid {
    const g: Grid = [];
    for (let r = 0; r < this.rows; r++) {
      g.push([]);
      for (let c = 0; c < this.cols; c++) g[r].push(this.kindAt(r, c));
    }
    return g;
  }

  /**
   * Fills every empty open cell with a random kind so that no three equal
   * tiles line up (checks left and up; later cells see earlier ones).
   */
  fillRandom(kinds: readonly TileKind[], rng: Rng): void {
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const cell = this.get(r, c);
        if (cell.hole || cell.blocker || cell.piece) continue;
        const banned = new Set<TileKind>();
        const l1 = this.kindAt(r, c - 1);
        if (l1 && l1 === this.kindAt(r, c - 2)) banned.add(l1);
        const u1 = this.kindAt(r - 1, c);
        if (u1 && u1 === this.kindAt(r - 2, c)) banned.add(u1);
        // also look right/down in case of preset tiles
        const r1 = this.kindAt(r, c + 1);
        if (r1 && r1 === this.kindAt(r, c + 2)) banned.add(r1);
        if (l1 && l1 === r1) banned.add(l1);
        const d1 = this.kindAt(r + 1, c);
        if (d1 && d1 === this.kindAt(r + 2, c)) banned.add(d1);
        if (u1 && u1 === d1) banned.add(u1);
        const allowed = kinds.filter((k) => !banned.has(k));
        cell.piece = this.newPiece(rng.pick(allowed.length ? allowed : kinds));
      }
    }
  }
}

/** Convenience for the preview: a match-free random grid. */
export function createBoard(rows: number, cols: number, kinds: readonly TileKind[], rng: Rng): Grid {
  const b = new Board(rows, cols);
  b.fillRandom(kinds, rng);
  return b.toGrid();
}

/** True if any horizontal or vertical run of 3+ equal tiles exists. */
export function hasMatch(grid: Grid): boolean {
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      const t = grid[r][c];
      if (!t) continue;
      if (c >= 2 && grid[r][c - 1] === t && grid[r][c - 2] === t) return true;
      if (r >= 2 && grid[r - 1][c] === t && grid[r - 2][c] === t) return true;
    }
  }
  return false;
}
